import React, { useState, useEffect } from "react";
import { Routes, Route, Switch, withRouter } from "react-router-dom";
import request from "request"
import Flash from "flash"

import Card from "Card"
import Icon from "Icon"
import Button from "Button"

const OrderView = ({
  store,
  terminal,
  order,
  clearOrder,
  fetchOrder,
  productCount,
  requestCredit,
  removeCredits,
  removeProduct,
  removeDiscount,
  removeGratuity,
  isModifyingOrder,
  setIsPaymentActive,
  putOnExistingTab,
  putOnNewTab,
  chargesCustomerFees,
  hasPrepaidEnabled,
  allowTaxToggle,
}) => {
  const [ isLoading, setIsLoading ] = useState(false)
  const [ hasCredits, setHasCredits ] = useState(false)
  const [ isSendingToPrinter, setIsSendingToPrinter ] = useState(false)

  const requestPayment = () => {
    if(!order || !order.id) return false

    setIsPaymentActive(true)
  }

  const printReceipt = () => {
    if(!order || !order.id) return false

    setIsSendingToPrinter(true)
    const req = request.post(`/orders/${order.id}/print_receipt`)
    req.then(({data}) => {
      setTimeout(() => {
        Flash.broadcast("success", "Sent to printer")
        setIsSendingToPrinter(false)
      }, 1000)
    })
    req.catch(() => {
      Flash.broadcast("error", "Failed to send to printer")
      setIsSendingToPrinter(false)
    })
  }

  const renderDiscountRows = () => {
    if (!order?.discountLineItems?.length) return null;
    return order.discountLineItems.map((discountLineItem, i) => {
      const discount = discountLineItem.discount;
      return (
        <tr key={"discount" + discountLineItem.id}>
        <td>
          <Button
          onClick={() => removeDiscount(order.id, discount.id)}
          color="red"
          className="is-outlined"
          text={<Icon icon="trash" />}
          disabled={isModifyingOrder}
          />
        </td>
        <td>{discountLineItem.quantity}</td>
        <td>
          <span className="has-text-weight-bold"> { discount.name }</span>
          <span className="has-text-weight-bold is-pulled-right">
          { discount.discountUnit === "percent"
            ? `${discount.discountAmount}%`
            : `$${discount.discountAmount}` }
          </span>
        </td>
        </tr>
      );
    });
  };

  const renderGratuityHeaderRow = () => {
    if (!order?.gratuityLineItems?.length) return null;
    return (
      <tr>
        <td colSpan="3" className="has-text-weight-bold">Gratuities</td>
      </tr>
    );
  };

  const renderGratuityRows = () => {
    if (!order?.gratuityLineItems?.length) return null;
    return order.gratuityLineItems.map((gratuityLineItem) => {
      const gratuity = gratuityLineItem.gratuity;
      return (
        <tr key={"gratuity" + gratuityLineItem.id}>
          <td>
            <Button
              onClick={() => removeGratuity(order.id, gratuity.id)}
              color="red"
              className="is-outlined"
              text={<Icon icon="trash" />}
              disabled={isModifyingOrder}
            />
          </td>
          <td>{gratuityLineItem.quantity}</td>
          <td>
            <span className="has-text-weight-bold"> { gratuity.name }</span>
            <span className="has-text-weight-bold is-pulled-right">
            { gratuity.gratuityUnit === "percent"
              ? `${gratuity.gratuityAmount}%`
              : `$${gratuity.gratuityAmount}` }
            </span>
          </td>
        </tr>
      );
    });
  };

  const renderPrepaidCreditHeaderRow = () => {
    if (!order?.prepaidCreditLineItems?.length) return null;
    return (
      <tr>
        <td colSpan="3" className="has-text-weight-bold">Prepaid Credits</td>
      </tr>
    );
  };

  const renderPrepaidCreditRows = () => {
    if (!order?.prepaidCreditLineItems?.length) return null;
    return order.prepaidCreditLineItems.map((creditLineItem, i) => {
      const credit = creditLineItem.prepaidCredit;
      return (
        <tr key={"credit" + creditLineItem.id}>
          <td>
            <Button
              onClick={() => removeCredits(credit.id)}
              color="red"
              className="is-outlined"
              text={<Icon icon="trash" />}
              disabled={isModifyingOrder}
            />
          </td>
          <td>1</td>
          <td>
            <span className="has-text-weight-bold">{credit.code}</span>
            <span className="has-text-weight-bold is-pulled-right">
              {creditLineItem.appliedAmount}
            </span>
          </td>
        </tr>
      );
    });
  };

  const toggleTax = () => {
    if (!order || !order.id) return false;

    const nextValue = !order.taxDisabled;

    const req = request.patch(`/carts/${order.id}/toggle_tax`, {
      tax_disabled: nextValue,
    });

    req.then(() => {
      if (typeof fetchOrder === "function") {
        fetchOrder();
      }
    });
  };

  const renderDiscountHeaderRow = () => {
    if (!order?.discountLineItems?.length) return null;
    return (
    <tr>
      <td colSpan="3" className="has-text-weight-bold">Discounts</td>
    </tr>
    );
  };

  const renderProductRows = () => {
    if (!order || order.products.length === 0) {
      return (
        <tr>
        <td colSpan="3" className="my-2 is-italic has-text-centered">Order is Empty</td>
        </tr>
      );
    }
    return order.products.map((lineItem, i) => (
    <tr key={"li" + i}>
      <td>
      <Button
        onClick={() => removeProduct(lineItem.id, lineItem.quantity)}
        color="red"
        className="is-outlined"
        text={<Icon icon="trash" />}
        disabled={isModifyingOrder}
      />
      </td>
      <td className="is-narrow">{lineItem.quantity}</td>
      <td style={{ width: "100%" }}>
      <p className="has-text-weight-bold">
        {lineItem.name === "Custom Amount"
        ? [lineItem.name, lineItem.price].join(" $")
        : lineItem.name}
      </p>
      {lineItem.notes && lineItem.notes.length > 0 && (
        <p style={{wordBreak: "break-word"}}>{lineItem.notes}</p>
      )}
      {lineItem.modifiers.length > 0 && (
        <p>{lineItem.modifiers.map((m) => m.name).join(", ")}</p>
      )}
      </td>
    </tr>
    ));
  };

  const renderTable = () => {
    return (
      <table className="table is-compact is-fullwidth">
      <thead>
        <tr>
        <th></th>
        <th>Qty</th>
        <th>Product</th>
        </tr>
      </thead>
      <tbody>
        {renderProductRows()}
        {renderDiscountHeaderRow()}
        {renderDiscountRows()}
        {renderGratuityHeaderRow()}
        {renderGratuityRows()}
        {renderPrepaidCreditHeaderRow()}
        {renderPrepaidCreditRows()}
      </tbody>
      </table>
    );
  };

  const renderButtons = () => (
    <div className="is-flex">
      <Button
        onClick={clearOrder}
        text={order?.isOnTab ? "Park Tab" : "Clear Order"}
        className="is-outlined"
        color={order?.isOnTab ? "info" : "red"}
        disabled={!(order?.products?.length > 0)}
      />
      <Button
        onClick={requestPayment}
        disabled={!order || order.products?.length === 0}
        text="Process Payment"
        color="green"
        className="ml-auto"
      />
    </div>
  );

  const renderTabButtons = () => (
    <div className="is-flex mt-4">
      { order?.isOnTab ? (
        <h4>On Tab for {order.formattedCustomer}</h4>
      ) : (
        <>
          <Button
            onClick={putOnNewTab}
            disabled={!order || order.products?.length === 0}
            text="Put on New Tab"
            color="grey"
          />
          <Button
            onClick={putOnExistingTab}
            disabled={!order || order.products?.length === 0}
            text="Put on Existing Tab"
            color="grey"
            className="ml-auto"
          />
        </>
      )}
    </div>
  );

  return (
    <div className="OrderView">
      <Card title="Order" actions={
        <>
          { terminal?.hasPrinter && order?.products?.length > 0 &&
            <Button
              onClick={printReceipt}
              color="link"
              className="is-outlined mr-2 "
              text={<><Icon icon="print" className="mr-2" />Print</>}
              loading={isSendingToPrinter}
              disabled={isModifyingOrder || !order?.id}
            />
          }
          { allowTaxToggle && order &&
            <Button
              onClick={toggleTax}
              color={order.taxDisabled ? "info" : "grey"}
              className="is-outlined mr-2"
              text={order.taxDisabled ? "Enable Tax" : "Disable Tax"}
              disabled={isModifyingOrder || !order.id}
            />
          }
          { hasPrepaidEnabled &&
            <Button
              onClick={requestCredit}
              color="link"
              className="ml-auto is-outlined"
              text="Add Credit"
              disabled={isModifyingOrder || !order?.id}
            />
          }
        </>
        }>
        <div className="is-flex">
        </div>
        <div>
          {renderTable()}

          <div className="has-text-right mb-4">
            <table className="has-text-right" style={{width: "100%"}}>
              <tbody>
                <tr>
                  <th style={{width: "100%"}} />
                  <td />
                </tr>
                <tr>
                  <th className="pr-2">Items Subtotal</th>
                  <td> ${ order?.subtotal || "0.00" }</td>
                </tr>
                <tr>
                  <th className="pr-2">Sales Tax</th>
                  <td>${ order?.taxes || "0.00" }</td>
                </tr>
                { order?.gratuityLineItems?.length > 0 &&
                  <tr>
                    <th className="pr-2">Gratuity</th>
                    <td>{ order?.gratuityAmount || "0.00" }</td>
                  </tr>
                }
                { order?.hasPrepaidCredit &&
                  <tr>
                    <th className="pr-2">Credits Applied</th>
                    <td>{ order?.totalPrepaidCreditAmount || order?.prepaidCreditAmount || "0.00" }</td>
                  </tr>
                }
                <tr>
                  <th className="pr-2">Total Due</th>
                  <td>${ order?.total || "0.00" }</td>
                </tr>
              </tbody>
            </table>
          </div>

          {renderButtons()}
          {renderTabButtons()}
        </div>
      </Card>
    </div>
  );
}

export default OrderView
